<template>
  <div class="min-h-screen flex flex-col items-center justify-center gap-6 px-4">
    <!-- Branding -->
    <div class="flex flex-col items-center gap-2">
      <img src="/eqsys-white.svg" alt="eqSys" class="h-12 w-auto" />
      <div class="text-sm" style="color:var(--color-muted)">
        <span v-if="tenantName">{{ tenantName }} · </span>{{ $t('login.subtitle') }}
      </div>
    </div>

    <div :class="['max-w-md w-full p-6 shadow-lg z-40 card', shake ? 'shake' : '']">
      <h2 class="text-2xl font-semibold mb-4" style="color:var(--color-text)">
        <span v-if="tenantName">{{ $t('login.signInTo', { tenant: tenantName }) }}</span>
        <span v-else>{{ $t('login.signIn') }}</span>
      </h2>

      <div v-if="tenantSlug" class="mb-4 flex flex-wrap items-center gap-2 text-sm">
        <span>{{ $t('login.organisation', { organisation: tenantSlug }) }}</span>
        <button type="button" class="btn ghost small" :disabled="loading" data-test="change-organisation" @click="changeOrganisation">{{ $t('login.changeOrganisation') }}</button>
      </div>
      <div v-else-if="rememberedOrganisation" class="mb-4 flex flex-wrap gap-2">
        <button type="button" class="btn secondary small" :disabled="loading" @click="useRememberedOrganisation">{{ $t('login.continueWithOrganisation', { organisation: rememberedOrganisation.name }) }}</button>
        <button type="button" class="btn ghost small" :disabled="loading" data-test="forget-organisation" @click="forgetRememberedOrganisation">{{ $t('login.forgetOrganisation') }}</button>
      </div>

      <form id="login-form" @submit.prevent="onSubmit" class="space-y-4">

        <!-- Stage: email discovery -->
        <div v-if="stage === 'email'">
          <BaseFormField :label="$t('login.email')" :error="emailError">
            <BaseInput
              ref="emailInput"
              v-model="email"
              type="email"
              autocomplete="email"
              autofocus
              :placeholder="$t('login.emailPlaceholder')"
            />
          </BaseFormField>
          <!-- Hidden submit so Enter in the email input still triggers the form -->
          <button type="submit" class="sr-only" aria-hidden="true" tabindex="-1"></button>
        </div>

        <!-- Stage: password login -->
        <div v-else-if="stage === 'password'">
          <p class="mb-3 text-sm" style="color:var(--color-muted)">
            {{ $t('login.signInAs', { email }) }}
          </p>
          <BaseFormField :label="$t('login.password')" :error="error || ''">
            <BaseInput
              ref="passwordInput"
              v-model="password"
              type="password"
              autocomplete="current-password"
            />
          </BaseFormField>
          <div class="mt-4 flex items-center justify-between gap-2">
            <button type="button" class="btn" @click="goBack">{{ $t('login.back') }}</button>
            <div class="flex items-center gap-3">
              <router-link
                :to="{ path: '/login/forgot', query: email ? { email } : undefined }"
                class="text-sm"
                style="color:var(--color-muted)"
              >{{ $t('login.forgotPassword') }}</router-link>
              <button type="submit" class="btn primary" :disabled="loading || !isPasswordValid">{{ $t('login.signIn') }}</button>
            </div>
          </div>
        </div>

        <!-- Stage: tenant selection (multiple tenants returned after password login) -->
        <div v-else-if="stage === 'tenant'">
          <p class="mb-3 text-sm" style="color:var(--color-muted)">{{ $t('login.selectTenant') }}</p>
          <BaseRadioGroup
            v-model="selectedTenantSlug"
            :options="tenantOptions"
          />
          <div v-if="error" class="text-sm text-red-400 mt-2">{{ error }}</div>
          <div class="mt-4 flex gap-2">
            <button type="button" class="btn" @click="stage = 'password'">{{ $t('login.back') }}</button>
            <button
              type="button"
              class="btn primary ml-auto"
              :disabled="!selectedTenantSlug || loading"
              @click="loginWithSelectedTenant"
            >{{ $t('login.continue') }}</button>
          </div>
        </div>

        <!-- Stage: OIDC login (or step-up) -->
        <div v-else-if="stage === 'oidc'">
          <p class="mb-3 text-sm" style="color:var(--color-muted)">{{ $t('login.authenticateVia') }}</p>
          <div v-if="oidcReason" class="mb-3 text-sm" style="color:var(--color-accent)">{{ oidcReason }}</div>
          <div class="space-y-2">
            <button
              v-for="p in oidcProviders"
              :key="p.id"
              type="button"
              class="btn primary w-full"
              :disabled="loading"
              @click="startOidc(p.id)"
            >{{ $t('login.signInWith', { provider: p.name }) }}</button>
          </div>
          <div class="mt-4 flex items-center gap-3">
            <button type="button" class="btn" @click="goBack">{{ $t('login.back') }}</button>
            <button
              v-if="allowPasswordFallback"
              type="button"
              class="btn ghost ml-auto text-sm"
              @click="stage = 'password'"
            >{{ $t('login.usePasswordInstead') }}</button>
          </div>
        </div>

        <BaseFormField :hint="$t('login.rememberOrganisationHint')">
          <BaseCheckbox v-model="rememberOrganisation" :label="$t('login.rememberOrganisation')" :disabled="loading" />
        </BaseFormField>
      </form>

      <!-- Analytics consent — shown on the email stage until the user chooses -->
      <AnalyticsConsentBanner v-if="consentPending && stage === 'email'" />

      <!-- Analytics is optional and never blocks sign-in. -->
      <div v-if="stage === 'email'" class="mt-4 flex justify-end">
        <button
          type="submit"
          form="login-form"
          class="btn primary"
          :disabled="loading || !isEmailValid"
        >{{ $t('login.continue') }}</button>
      </div>

      <div v-if="loading" class="mt-4 text-sm" style="color:var(--color-muted)">{{ $t('login.processing') }}</div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth.js'
import { usePostHog } from '@/composables/usePostHog.js'
import AnalyticsConsentBanner from '@/components/base/AnalyticsConsentBanner.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseRadioGroup from '@/components/base/BaseRadioGroup.vue'
import BaseCheckbox from '@/components/base/BaseCheckbox.vue'
import { getRememberedOrganisation, forgetOrganisation } from '@/composables/rememberedOrganisation.js'

const { consentPending } = usePostHog()
const rememberOrganisation = ref(false)
const rememberedOrganisation = ref(getRememberedOrganisation())
const { t } = useI18n()

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

// ── Form state ────────────────────────────────────────────────────────────
const email = ref('')
const password = ref('')
const stage = ref('email')      // 'email' | 'password' | 'tenant' | 'oidc'
const loading = ref(false)
const error = ref(null)
const emailError = ref('')
const shake = ref(false)

const oidcProviders = ref([])   // Array<{ id, name }>
const oidcReason = ref(null)
const tenantSlug = ref(null)    // slug resolved during discovery / login
const tenantName = ref('')
const tenants = ref([])         // for tenant-selection stage
const selectedTenantSlug = ref('')
const hoveredTenant = ref(null)
const allowPasswordFallback = ref(false)

const emailInput = ref(null)
const passwordInput = ref(null)

const tenantOptions = computed(() =>
  tenants.value.map((tenant) => ({
    value: tenant.slug,
    label: tenant.has_session === false
      ? `${tenant.name} (${tenant.slug}) — ${t('login.stepUpRequired')}`
      : `${tenant.name} (${tenant.slug})`,
  }))
)

// ── Computed ──────────────────────────────────────────────────────────────
const isEmailValid = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim()))
const isPasswordValid = computed(() => password.value.trim().length > 0)

// Auto-focus relevant input when stage changes
watch(stage, async (val) => {
  await nextTick()
  if (val === 'password') passwordInput.value?.focus()
  else if (val === 'email') emailInput.value?.focus()
})

// ── Bootstrap from URL params ─────────────────────────────────────────────
onMounted(() => {
  const qTenant = route.query?.tenant
  const qStage = route.query?.stage
  if (qTenant) tenantSlug.value = qTenant
  if (qStage && ['email', 'password', 'oidc'].includes(qStage)) stage.value = qStage

  // /tenant/:tenantSlug route — pre-populate slug
  const slugParam = route.params?.tenantSlug
  if (slugParam) tenantSlug.value = slugParam
})

function useRememberedOrganisation() {
  tenantSlug.value = rememberedOrganisation.value.slug
  rememberOrganisation.value = true
}

function forgetRememberedOrganisation() {
  forgetOrganisation()
  rememberedOrganisation.value = null
  rememberOrganisation.value = false
}

watch(rememberOrganisation, (remember) => {
  if (!remember) forgetRememberedOrganisation()
})

async function changeOrganisation() {
  tenantSlug.value = null
  password.value = ''
  error.value = null
  goBack()
  // Replace explicit tenant context while retaining the requested destination.
  const { tenant: _tenant, stage: _stage, ...query } = route.query
  await router.replace({ path: '/login', query })
}

// ── Helpers ───────────────────────────────────────────────────────────────
function triggerShake() {
  shake.value = true
  password.value = ''
  setTimeout(() => { shake.value = false }, 500)
}

function goBack() {
  oidcReason.value = null
  error.value = null
  stage.value = 'email'
}

async function onSubmit() {
  if (stage.value === 'email') await discover()
  else if (stage.value === 'password') await doPasswordLogin()
}

// ── Email Discovery ───────────────────────────────────────────────────────
async function discover() {
  emailError.value = ''
  if (!isEmailValid.value) { emailError.value = t('login.invalidEmail'); return }
  loading.value = true
  error.value = null
  try {
    const res = await authStore.discoverEmail(email.value.trim())
    if (res.auth_method === 'oidc' || res.auth_method === 'choice') {
      oidcProviders.value = res.oidc_providers || []
      if (res.tenant_slug) tenantSlug.value = res.tenant_slug
      // Enable "Use password instead" only when backend advertises a choice
      allowPasswordFallback.value = res.auth_method === 'choice'
      stage.value = 'oidc'
    } else {
      stage.value = 'password'
    }
  } catch (e) {
    emailError.value = e.data?.detail || e.data?.error || t('login.emailLookupFailed')
  } finally {
    loading.value = false
  }
}

// ── Password Login ────────────────────────────────────────────────────────
async function doPasswordLogin(overrideTenant) {
  error.value = null
  if (!isPasswordValid.value) return
  loading.value = true
  try {
    const slug = overrideTenant ?? tenantSlug.value ?? undefined
    const res = await authStore.loginWithPassword(email.value.trim(), password.value, slug, rememberOrganisation.value)

    // Backend needs tenant selection
    if (res.requires_tenant_selection) {
      tenants.value = res.tenants || []
      selectedTenantSlug.value = ''
      stage.value = 'tenant'
      return
    }

    // Backend requires step-up (higher AAL)
    if (res.step_up_required) {
      oidcProviders.value = (res.methods || [])
        .filter(m => m.type === 'oidc')
        .map(m => ({ id: m.provider_id, name: m.name }))
      tenantSlug.value = res.tenant_slug || slug
      oidcReason.value = t('login.oidcRequired')
      allowPasswordFallback.value = false
      stage.value = 'oidc'
      return
    }

    // Success — auth store already called setTokens; navigate onward
    const redirect = route.query?.redirect
    router.replace(redirect && redirect !== '/login' ? redirect : '/')
  } catch (e) {
    const status = e.status
    const data = e.data || {}

    if (status === 401 || status === 400) {
      error.value = data.error || data.detail || t('login.invalidCredentials')
      triggerShake()
    } else if (status === 403 && data.step_up_required) {
      oidcProviders.value = (data.methods || [])
        .filter(m => m.type === 'oidc')
        .map(m => ({ id: m.provider_id, name: m.name }))
      tenantSlug.value = data.tenant_slug ?? overrideTenant ?? tenantSlug.value
      oidcReason.value = data.error || t('login.oidcRequired')
      allowPasswordFallback.value = false
      stage.value = 'oidc'
    } else {
      error.value = data.error || data.detail || t('login.unexpectedError')
    }
  } finally {
    loading.value = false
  }
}

// ── Tenant Selection ──────────────────────────────────────────────────────
async function loginWithSelectedTenant() {
  if (!selectedTenantSlug.value) return
  await doPasswordLogin(selectedTenantSlug.value)
}

// ── OIDC ──────────────────────────────────────────────────────────────────
async function startOidc(providerId) {
  loading.value = true
  error.value = null
  try {
    await authStore.loginWithOIDC(tenantSlug.value, providerId, rememberOrganisation.value)
    // loginWithOIDC redirects window.location.href — if we reach here, no URL was returned
    error.value = t('login.noRedirectUrl')
    loading.value = false
  } catch (e) {
    error.value = e.data?.error || t('login.oidcFailed')
    loading.value = false
  }
}
</script>
